//dependencies
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";

import { DropDownMenu, DropDownMenuOption, Text } from "@performyard/py-components";
//components
import { GetUserResponseJSON } from "@performyard/service-contracts";
import { isSyntheticEmail } from "@performyard/utils-isomorphic";

import AddFormModal from "../../../../../components/AddFormModal";
import Icon from "../../../../../components/Icon/Icon";
import ReviewTemplateModal from "../../../../../components/ReviewTemplateModal";

import {
	useFeedbackDrawerStore,
	useRequestFeedbackDrawerStore,
} from "../../../../../containers/FeedbackDrawers/FeedbackDrawerStore";

//hooks
import { useCompanyFunction, useEasyAddReviews } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

//selectors
import { getCompanySettingsContext } from "../../../../../selectors/settings";

type OptionsDropdownProps = {
	employeeId: string;
	currentUserId: string;
	userInfo?: GetUserResponseJSON;
	employeeDetailsRbac?: string[];
	goalRbac?: string[];
	reviewRbac?: string[];
	isInviting: boolean;
	isAddFormModalOpen: boolean;
	isCreateReviewModalOpen: boolean;
	onSendInvite: () => void;
	onOpenModal: (modal: "deactivate" | "reactivate" | "add360Form" | "createReview") => void;
	onCloseModal: () => void;
};

export const OptionsDropdown = ({
	employeeId,
	currentUserId,
	userInfo,
	employeeDetailsRbac,
	goalRbac,
	reviewRbac,
	isInviting,
	isAddFormModalOpen,
	isCreateReviewModalOpen,
	onSendInvite,
	onOpenModal,
	onCloseModal,
}: OptionsDropdownProps) => {
	const navigate = useNavigate();
	const companySettings = useSelector(getCompanySettingsContext);
	const { leaveFeedback } = useFeedbackDrawerStore();
	const { requestFeedback } = useRequestFeedbackDrawerStore();

	const { companyFunction: feedbackFunction, attributes: feedbackAttributes } = useCompanyFunction("feedback");
	const { companyFunction: goalsFunction } = useCompanyFunction("goals");
	const { companyFunction: reviewsFunction } = useCompanyFunction("reviews");
	const { value: ffFeedbackEnabled } = useFeatureFlagQuery("ff-is-feedback-enabled", true);
	const { value: ffGoalsEnabled } = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const { value: ffReviewsEnabled } = useFeatureFlagQuery("ff-is-reviews-enabled", false);
	const { value: ff360Reviews } = useFeatureFlagQuery("ff-is-three-sixty-reviews-enabled", false);

	// null is the modal's own default, and the wizard reads it as an ad hoc review
	const [reviewTemplateId, setReviewTemplateId] = useState<string | null>(null);

	const easyAddAllowed = companySettings.getIn(["reviews", "easy_add"], false);
	const add360FormButtonLabel = companySettings.getIn(["add-360-form-button-label"], true);
	// Same source FeedbackDrawers uses to mount the request panel, so the option can never
	// open a drawer that isn't mounted.
	const allowRequestFeedback = !!feedbackAttributes?.allowRequestFeedback;

	const { data: easyAddData } = useEasyAddReviews(
		currentUserId,
		employeeId,
		{ limit: 10, skip: 0 },
		!currentUserId || !employeeId || !easyAddAllowed || !ff360Reviews
	);

	const isSelf = employeeId === currentUserId;
	const employeeName = userInfo?.name || "";

	const isDeactivated = !!userInfo?.deactivated;

	const canSendInvite = !!employeeDetailsRbac?.includes("employee_details:sendInvite") && !userInfo?.lastSeen;
	const canDeactivate = !!employeeDetailsRbac?.includes("employee_details:deactivateEmployee") && !isSelf;
	const show360Button = !!(easyAddAllowed && easyAddData?.reviews?.length && ff360Reviews);
	// downstream feedback/goal/review flows don't support deactivated assignees
	const feedbackEnabled = feedbackFunction === "enabled" && ffFeedbackEnabled && !isDeactivated;
	const canCreateGoal =
		goalsFunction === "enabled" && ffGoalsEnabled && !!goalRbac?.includes("goals:createGoal") && !isDeactivated;
	// reviews:create is scoped to the viewed employee; reviews:viewCreateReviewButton is not
	const canCreateReview =
		reviewsFunction === "enabled" && ffReviewsEnabled && !!reviewRbac?.includes("reviews:create") && !isDeactivated;

	const handleGiveFeedback = () => {
		if (isSelf) {
			leaveFeedback();
			return;
		}
		leaveFeedback({ requestData: { requestedTo: { name: employeeName, _id: employeeId } } });
	};

	const handleRequestFeedback = () => {
		if (isSelf) {
			requestFeedback();
			return;
		}
		requestFeedback({ requestedTo: { name: employeeName, _id: employeeId } });
	};

	const handleCreateReview = () => {
		onCloseModal();
		navigate(`/start-review/${reviewTemplateId ?? ""}?subject=${employeeId}`);
	};

	const options: DropDownMenuOption[] = [];

	if (canSendInvite) {
		options.push({
			key: "send-invite",
			label: userInfo?.invited ? "Resend Invite" : "Send Invite",
			onClick: onSendInvite,
			disabled: isSyntheticEmail(userInfo?.email) || isInviting,
		});
	}

	if (canDeactivate) {
		options.push(
			userInfo?.deactivated
				? { key: "reactivate", label: "Reactivate Employee", onClick: () => onOpenModal("reactivate") }
				: { key: "deactivate", label: "Deactivate Employee", onClick: () => onOpenModal("deactivate") }
		);
	}

	if (show360Button) {
		options.push({
			key: "add-360-form",
			label: add360FormButtonLabel ? "+ Add 360 Form" : "+ Add Form",
			onClick: () => onOpenModal("add360Form"),
		});
	}

	if (feedbackEnabled) {
		options.push({ key: "give-feedback", label: "Give Feedback", onClick: handleGiveFeedback });
		if (allowRequestFeedback) {
			options.push({ key: "request-feedback", label: "Request Feedback", onClick: handleRequestFeedback });
		}
	}

	if (canCreateGoal) {
		options.push({
			key: "create-goal",
			label: "Create Goal",
			onClick: () => navigate(`/goal/wizard/?assignee=${employeeId}`),
		});
	}

	if (canCreateReview) {
		options.push({
			key: "create-review",
			label: "Create Review",
			onClick: () => {
				// the modal remounts with None selected, so the previous choice can't carry over
				setReviewTemplateId(null);
				onOpenModal("createReview");
			},
		});
	}

	return (
		<>
			{show360Button && isAddFormModalOpen && (
				<AddFormModal
					userID={currentUserId}
					employeeID={employeeId}
					handleClose={onCloseModal}
					reviews={easyAddData}
				/>
			)}
			{canCreateReview && isCreateReviewModalOpen && (
				<ReviewTemplateModal
					handleTemplateInputChange={setReviewTemplateId}
					handleModalCancel={onCloseModal}
					createReview={handleCreateReview}
				/>
			)}
			{options.length > 0 && (
				<DropDownMenu
					options={options}
					placement="bottom"
					bordered
					className="w-full"
					testid="employee-details-options-dropdown"
				>
					<span className="flex items-center gap-1">
						<Text size="paragraph-3" weight="semi-bold">
							Options
						</Text>
						<Icon size="small" icon={faAngleDown} />
					</span>
				</DropDownMenu>
			)}
		</>
	);
};
